import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage } from "../api/client";
import { records } from "../api/endpoints";
import type { RecordFilter, RecordOut } from "../api/types";
import { ConfirmDialog } from "../components/Modal";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toast";
import { Button, EmptyState, ErrorNote, Input, PageHeader, Select, Spinner, cx } from "../components/ui";
import { formatCount, formatUsd } from "../lib/decimal";
import { EMPTY_FILTER, FilterBar, activeFilterCount, toCriteria, type FilterDraft } from "./records/FilterBar";
import { RecordFormModal, type FormMode } from "./records/RecordFormModal";
import { RecordsCards, RecordsTable, type FlagHeaderState } from "./records/RecordsTable";

const PAGE_SIZES = [50, 100, 200, 500];
const SEARCH_DEBOUNCE_MS = 300;

type FlagName = "done" | "rejected";

const FLAG_LABELS: Record<FlagName, { noun: string; cleared: string }> = {
  done: { noun: "Done", cleared: "Not Done" },
  rejected: { noun: "Rejected", cleared: "Not Rejected" },
};

interface PendingFlag {
  flag: FlagName;
  value: boolean;
  /** Either explicit ids, or "everything the current filter matches". */
  target: { kind: "ids"; ids: number[] } | { kind: "matching"; count: number };
  message: string;
}

export function RecordsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_FILTER);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [orderBy, setOrderBy] = useState("created_at");
  const [descending, setDescending] = useState(true);

  const [formMode, setFormMode] = useState<FormMode>("create");
  const [formRecord, setFormRecord] = useState<RecordOut | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingFlag, setPendingFlag] = useState<PendingFlag | null>(null);
  const [pendingDelete, setPendingDelete] = useState<RecordOut | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  // Typing in the search box shouldn't fire a query per keystroke — and a
  // pasted list of 200 tickets is one change, not 200.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const criteria = useMemo<RecordFilter>(() => toCriteria(draft, search), [draft, search]);

  // Changing what is matched starts back at page 1 of the new result set.
  const criteriaKey = JSON.stringify(criteria);
  const previousCriteria = useRef(criteriaKey);
  useEffect(() => {
    if (previousCriteria.current !== criteriaKey) {
      previousCriteria.current = criteriaKey;
      setPage(1);
    }
  }, [criteriaKey]);

  const pageQuery = useQuery({
    queryKey: ["records", "page", criteria, page, pageSize, orderBy, descending],
    queryFn: ({ signal }) =>
      records.search({ criteria, page, page_size: pageSize, order_by: orderBy, descending }, signal),
  });

  const totalsQuery = useQuery({
    queryKey: ["records", "totals", criteria],
    queryFn: ({ signal }) => records.totals(criteria, signal),
  });

  // The header tick reports on the whole matched set, so it needs the ticked
  // count for that set — not just for the page on screen. Skipped entirely
  // when the Status filter already pins this same axis (e.g. "Not Done"):
  // querying "how many are done" while also filtering on is_done would answer
  // a different question than what's on screen (see headerState below), so
  // there's nothing useful to fetch — the axis is already fully determined.
  const doneAxisFixed = criteria.is_done === true || criteria.is_done === false;
  const rejectedAxisFixed = criteria.is_rejected === true || criteria.is_rejected === false;

  const doneTotals = useQuery({
    queryKey: ["records", "totals", { ...criteria, is_done: true }],
    queryFn: ({ signal }) => records.totals({ ...criteria, is_done: true }, signal),
    enabled: !doneAxisFixed,
  });
  const rejectedTotals = useQuery({
    queryKey: ["records", "totals", { ...criteria, is_rejected: true }],
    queryFn: ({ signal }) => records.totals({ ...criteria, is_rejected: true }, signal),
    enabled: !rejectedAxisFixed,
  });

  const rows = pageQuery.data?.items ?? [];
  const total = pageQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // The delete-selection is scoped to what's on screen, so it shouldn't
  // survive a page turn or a filter change — otherwise a tick made on page 1
  // could silently still be armed after navigating to page 3.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [criteriaKey, page, pageSize, orderBy, descending]);

  /**
   * Tri-state for one column's header tick.
   *
   * `fixedValue` is that column's OWN constraint from the Status filter, if
   * any (e.g. picking "Not Done" sets is_done=false). When it's set, the
   * visible set is already homogeneous on this axis by construction — every
   * row shares the same value — so the state is exactly determined without a
   * count at all. Ignoring this and always asking "how many (matching
   * everything else) have this flag set to true" would answer a question
   * about a *different* set than the one on screen whenever the user's own
   * filter already pins the opposite value, and could show an impossible
   * "some are ticked" state on a page where literally none are.
   */
  function headerState(
    fixedValue: boolean | null | undefined,
    ticked: number | undefined,
  ): FlagHeaderState {
    if (fixedValue === true) return { checked: total > 0, indeterminate: false, disabled: total === 0 };
    if (fixedValue === false) return { checked: false, indeterminate: false, disabled: total === 0 };
    const count = ticked ?? 0;
    return {
      checked: total > 0 && count === total,
      indeterminate: count > 0 && count < total,
      disabled: total === 0,
    };
  }

  // Selection covers only the rows currently on screen — never "everything
  // this filter matches" — because arming a group-delete for thousands of
  // hidden rows with a single header tick is a very different risk profile
  // than the Done/Rejected header ticks, which merely flip a reversible flag.
  const selectHeader: FlagHeaderState = {
    checked: rows.length > 0 && rows.every((r) => selectedIds.has(r.id)),
    indeterminate: rows.some((r) => selectedIds.has(r.id)) && !rows.every((r) => selectedIds.has(r.id)),
    disabled: rows.length === 0,
  };

  function onToggleSelectAll(value: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const row of rows) {
        if (value) next.add(row.id);
        else next.delete(row.id);
      }
      return next;
    });
  }

  function onToggleSelect(record: RecordOut, value: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (value) next.add(record.id);
      else next.delete(record.id);
      return next;
    });
  }

  const bulkDelete = useMutation({
    mutationFn: (ids: number[]) => records.removeBulk(ids),
    onSuccess: (result) => {
      toast.success(`${formatCount(result.changed)} records deleted`);
      setSelectedIds(new Set());
      refreshAll();
    },
    onError: (cause) => toast.error(errorMessage(cause)),
    onSettled: () => setConfirmBulkDelete(false),
  });

  function refreshAll() {
    void queryClient.invalidateQueries({ queryKey: ["records"] });
    void queryClient.invalidateQueries({ queryKey: ["analytics"] });
  }

  // -- flag mutations --------------------------------------------------------

  const applyFlag = useMutation({
    mutationFn: async (pending: PendingFlag) => {
      const { flag, value, target } = pending;
      if (target.kind === "matching") {
        return flag === "done"
          ? records.setDoneMatching(criteria, value)
          : records.setRejectedMatching(criteria, value);
      }
      return flag === "done"
        ? records.setDoneBulk(target.ids, value)
        : records.setRejectedBulk(target.ids, value);
    },
    onSuccess: (result, pending) => {
      const label = pending.value
        ? FLAG_LABELS[pending.flag].noun
        : FLAG_LABELS[pending.flag].cleared;
      if (result.changed > 0) {
        toast.success(`${formatCount(result.changed)} records marked as ${label}`);
      } else {
        toast.show(`Already marked as ${label}`, "info");
      }
      refreshAll();
    },
    onError: (cause) => toast.error(errorMessage(cause)),
    onSettled: () => setPendingFlag(null),
  });

  /**
   * Route a flag change through confirmation, matching the desktop page.
   * Marking Done/Rejected is a real sign-off and clearing undoes one someone
   * already made, so every change asks once before it applies — regardless
   * of how many records are involved.
   */
  function requestFlag(pending: PendingFlag) {
    const size =
      pending.target.kind === "matching" ? pending.target.count : pending.target.ids.length;
    if (size === 0) return;
    setPendingFlag(pending);
  }

  function onSetFlagOne(flag: FlagName, record: RecordOut, value: boolean) {
    const which = `ticket ${record.ticket_number}`;
    requestFlag({
      flag,
      value,
      target: { kind: "ids", ids: [record.id] },
      message: value
        ? `Mark ${which} as ${FLAG_LABELS[flag].noun}?`
        : `Remove the ${FLAG_LABELS[flag].noun} tick from ${which}?`,
    });
  }

  function onSetFlagMatching(flag: FlagName, value: boolean) {
    requestFlag({
      flag,
      value,
      target: { kind: "matching", count: total },
      message: value
        ? `Mark all ${formatCount(total)} matching records as ${FLAG_LABELS[flag].noun}?`
        : `Remove the ${FLAG_LABELS[flag].noun} tick from all ${formatCount(total)} matching records?`,
    });
  }

  const remove = useMutation({
    mutationFn: (record: RecordOut) => records.remove(record.id),
    onSuccess: (_result, record) => {
      toast.success(`Record #${record.id} deleted`);
      refreshAll();
    },
    onError: (cause) => toast.error(errorMessage(cause)),
    onSettled: () => setPendingDelete(null),
  });

  const exportRecords = useMutation({
    mutationFn: (format: "xlsx" | "csv") => records.export(criteria, format),
    onSuccess: (result) =>
      toast.success(
        result.rowCount === null
          ? `Exported ${result.filename}`
          : `Exported ${formatCount(result.rowCount)} records to ${result.filename}`,
      ),
    onError: (cause) => toast.error(errorMessage(cause)),
  });

  // -- form wiring -----------------------------------------------------------

  function openForm(mode: FormMode, record: RecordOut | null) {
    setFormMode(mode);
    setFormRecord(record);
    setFormOpen(true);
  }

  function onSort(key: string) {
    if (key === orderBy) {
      setDescending((current) => !current);
    } else {
      setOrderBy(key);
      setDescending(true);
    }
    setPage(1); // row 1 of the new order, not wherever the old page was
  }

  const filterCount = activeFilterCount(draft);

  const tableProps = {
    rows,
    orderBy,
    descending,
    onSort,
    doneHeader: headerState(criteria.is_done, doneTotals.data?.count),
    rejectHeader: headerState(criteria.is_rejected, rejectedTotals.data?.count),
    onSetFlagMatching,
    onSetFlagOne,
    onOpen: (record: RecordOut) => openForm("view", record),
    onEdit: (record: RecordOut) => openForm("edit", record),
    onDuplicate: (record: RecordOut) => openForm("duplicate", record),
    onDelete: (record: RecordOut) => setPendingDelete(record),
    selectedIds,
    selectHeader,
    onToggleSelectAll,
    onToggleSelect,
  };

  return (
    <>
      <PageHeader
        title="Records"
        subtitle="All compensation records"
        actions={
          <>
            <Button onClick={() => void pageQuery.refetch()} disabled={pageQuery.isFetching}>
              {pageQuery.isFetching ? <Spinner /> : <Icon name="refresh" />}
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              onClick={() => exportRecords.mutate("xlsx")}
              disabled={exportRecords.isPending}
              title="Export the filtered records to Excel"
            >
              {exportRecords.isPending ? <Spinner /> : <Icon name="download" />}
              <span className="hidden sm:inline">Excel</span>
            </Button>
            <Button
              onClick={() => exportRecords.mutate("csv")}
              disabled={exportRecords.isPending}
              title="Export the filtered records to CSV"
            >
              <Icon name="download" />
              <span className="hidden sm:inline">CSV</span>
            </Button>
            <Button variant="primary" onClick={() => openForm("create", null)}>
              <Icon name="plus" />
              New Record
            </Button>
          </>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-80">
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search or paste many tickets / accounts…"
            title={
              "Matches ticket, client, account or symbol.\n" +
              "Paste or type several values separated by spaces, commas or new lines to look them all up at once."
            }
            className="pr-8"
          />
          {searchInput ? (
            <button
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
              className="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer text-fg-muted hover:text-fg"
            >
              <Icon name="x" className="size-4" />
            </button>
          ) : null}
        </div>

        <Button
          onClick={() => setShowFilters((current) => !current)}
          className={cx(showFilters && "border-accent text-accent")}
        >
          <Icon name="filter" />
          Filters
          {filterCount > 0 ? (
            <span className="rounded bg-accent px-1.5 text-xs text-on-accent">{filterCount}</span>
          ) : null}
        </Button>

        {selectedIds.size > 0 ? (
          <Button variant="danger" onClick={() => setConfirmBulkDelete(true)}>
            <Icon name="trash" />
            Delete Selected ({formatCount(selectedIds.size)})
          </Button>
        ) : null}
      </div>

      {showFilters ? (
        <FilterBar draft={draft} onChange={setDraft} onClear={() => setDraft(EMPTY_FILTER)} />
      ) : null}

      {pageQuery.error ? (
        <div className="mb-3">
          <ErrorNote>{errorMessage(pageQuery.error)}</ErrorNote>
        </div>
      ) : null}

      {pageQuery.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6 text-fg-muted" />
        </div>
      ) : rows.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No records match"
            hint={
              search || filterCount > 0
                ? "Try widening the search or clearing some filters."
                : "Add a record, or import a batch from Bulk Paste."
            }
          />
        </div>
      ) : (
        <>
          <div className="hidden lg:block">
            <RecordsTable {...tableProps} />
          </div>
          <div className="lg:hidden">
            <RecordsCards {...tableProps} />
          </div>
        </>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-fg-secondary">
          {totalsQuery.data ? (
            <>
              {formatCount(totalsQuery.data.count)} records — total compensation{" "}
              <span className="num font-medium text-fg">
                {formatUsd(totalsQuery.data.compensation_sum)}
              </span>
            </>
          ) : (
            "…"
          )}
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-fg-muted">
            Rows
            <Select
              value={String(pageSize)}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className="w-20 py-1.5"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={String(size)}>
                  {size}
                </option>
              ))}
            </Select>
          </label>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
              aria-label="Previous page"
            >
              <Icon name="chevron-left" />
            </Button>
            <span className="num px-1 text-xs text-fg-secondary">
              Page {page} of {formatCount(pageCount)}
            </span>
            <Button
              variant="ghost"
              onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
              disabled={page >= pageCount}
              aria-label="Next page"
            >
              <Icon name="chevron-right" />
            </Button>
          </div>
        </div>
      </div>

      <RecordFormModal
        open={formOpen}
        mode={formMode}
        record={formRecord}
        onClose={() => setFormOpen(false)}
        onSaved={() => setFormOpen(false)}
        onRequestEdit={(record) => openForm("edit", record)}
        onRequestDuplicate={(record) => openForm("duplicate", record)}
        onRequestDelete={(record) => {
          setFormOpen(false);
          setPendingDelete(record);
        }}
        onRequestDone={(record) => {
          setFormOpen(false);
          onSetFlagOne("done", record, true);
        }}
      />

      <ConfirmDialog
        open={pendingFlag !== null}
        title={
          pendingFlag
            ? pendingFlag.value
              ? `Mark as ${FLAG_LABELS[pendingFlag.flag].noun}`
              : `Remove ${FLAG_LABELS[pendingFlag.flag].noun} Tick`
            : ""
        }
        message={pendingFlag?.message ?? ""}
        confirmLabel={pendingFlag?.value ? "Mark" : "Remove"}
        danger={pendingFlag ? !pendingFlag.value : false}
        busy={applyFlag.isPending}
        onConfirm={() => pendingFlag && applyFlag.mutate(pendingFlag)}
        onCancel={() => setPendingFlag(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete Record"
        message={
          pendingDelete ? (
            <>
              Delete record #{pendingDelete.id} (ticket {pendingDelete.ticket_number})?
              <br />
              This cannot be undone.
            </>
          ) : (
            ""
          )
        }
        confirmLabel="Delete"
        danger
        busy={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={confirmBulkDelete}
        title="Delete Selected Records"
        message={
          <>
            Delete {formatCount(selectedIds.size)} selected record
            {selectedIds.size === 1 ? "" : "s"}?
            <br />
            This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        danger
        busy={bulkDelete.isPending}
        onConfirm={() => bulkDelete.mutate([...selectedIds])}
        onCancel={() => setConfirmBulkDelete(false)}
      />
    </>
  );
}
